import { onlineManager } from '@tanstack/react-query';

import { ENV } from '@/config/env.config';

/** Au-delà, sur le réseau qu'on a, la requete d'un écran n'aboutirait pas non plus. */
const PROBE_TIMEOUT_MS = 8000;

/** Réponses reçues du serveur, par le canal ou par une requete : un compteur, pas une heure. */
let proofs = 0;
let probing = false;

/**
 * Joignabilité du serveur, au seul sens utile : « une requete a-t-elle une
 * chance d'aboutir maintenant ? ». Elle pilote le `onlineManager` de React
 * Query : serveur injoignable, les requetes se mettent en pause et les écrans
 * vivent sur le cache persisté ; joignable a nouveau, les requetes en pause
 * repartent d'elles-memes, sans rien demander aux écrans.
 *
 * Aucun module natif d'état réseau : la sonde, c'est le canal temps réel, qui
 * se reconnecte en boucle (socket.io) et dit donc mieux qu'une barre de signal
 * si le serveur répond. Une antenne pleine sur un réseau qui ne fait rien
 * passer est « hors ligne » ici : c'est le cas courant sur une connexion
 * congestionnée, et c'est le seul qui compte pour l'utilisateur. On démarre
 * joignable, et on ne conclut « injoignable » que sur une preuve : dans le
 * doute, on tente la requete, comme l'application l'a toujours fait.
 */
export function reportServerReachable(reachable: boolean): void {
  if (reachable) {
    proofs += 1;
  }
  if (onlineManager.isOnline() !== reachable) {
    onlineManager.setOnline(reachable);
  }
}

/**
 * Le canal temps réel vient de tomber, ou n'arrive pas à s'ouvrir : le serveur
 * est-il pour autant injoignable ?
 *
 * Pas forcément. Le canal peut échouer seul : un proxy qui ne laisse pas passer
 * le websocket, un chemin mal réglé. C'est arrivé le 30 octobre 2026 : l'API a
 * été servie sous un préfixe que le canal ne suivait pas, et toute
 * l'application s'est dite « hors ligne » alors que chaque requete aboutissait.
 * On le vérifie donc par la voie des requetes avant de le dire : une réponse,
 * quelle qu'elle soit, prouve que le serveur répond ; seule l'absence de
 * réponse met l'application en pause.
 *
 * Une preuve arrivée pendant la sonde (le canal revenu, une requete aboutie)
 * l'emporte sur l'échec de la sonde : on ne repasse pas hors ligne à contretemps.
 */
export function verifyServerReachable(): void {
  if (probing) {
    return;
  }
  probing = true;
  const proofsBefore = proofs;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  fetch(`${ENV.API_URL}/api/v1/health`, { cache: 'no-store', signal: controller.signal })
    .then(
      () => reportServerReachable(true),
      () => {
        if (proofs === proofsBefore) {
          reportServerReachable(false);
        }
      },
    )
    .finally(() => {
      clearTimeout(timer);
      probing = false;
    });
}
